<!-- Copyright: Seán I. O'Donoghue -->
<!-- Unlock Masterclass (2026-10-31; fullstack claude/UNLOCK-MASTERCLASS-SPEC.md): a variation
     of Videos/Unlock.vue listing every speaker of the locked Masterclass videos, once each,
     with their talks. The poster block's styles are Unlock.vue's (scoped there, copied). -->

<template>
  <main ref="rootRef" class="unlock-videos" :style="{'--line-size': lineSize}">
    <div v-if="isLoading" class="loading">Loading…</div>
    <div v-else class="unlock-content">
      <!-- the title exactly as Unlock Videos' (Seán, 2026-10-31) -->
      <h1 class="poster-title">
        <span>UNLOCK</span>
        <img src="/images/logo/vizbi-inline.png" alt="VIZBI" class="vizbi-logo" />
        <span>VIDEOS</span>
      </h1>
      <!-- the same title as Unlock Videos; the subtitle tells them apart, filling its line
           (Seán, 2026-10-31) -->
      <p class="poster-subtitle">
        <span ref="subtitleRef" class="subtitleFit">
          Data Visualization Principles &amp; Practices
        </span>
      </p>
      <p v-if="hasMasterclassAccess" class="intro text">
        You have access to all Masterclass videos.
      </p>
      <p v-else class="intro text">
        Registering for the online Masterclass gives access to every DataVis Masterclass talk
        since {{ firstYear }}:
        <template v-if="stats">{{ stats.videos }} videos, {{ stats.hours }} hours, by</template>
        <template v-else>{{ talkCount }} talks by</template>
        {{ speakers.length }} speakers.
      </p>
      <template v-if="!hasMasterclassAccess">
        <div class="reg-buttons">
          <a
            :href="registrationUrl('masterclass', '/Videos')"
            class="reg-btn"
            @click.prevent="redirectToRegistration('masterclass', '/Videos')">
            <strong>1</strong>
            Masterclass Registration
            <template v-if="stats">
              = <strong>{{ stats.videos }} videos, {{ stats.hours }} hours</strong>
            </template>
          </a>
        </div>
        <div class="info-rows">
          <router-link :to="`/${lastYear}/Registration`" class="info-row">
            How much does registration cost?
          </router-link>
          <Email to="vizbi-contact" class="info-row">For institutional access, contact us</Email>
        </div>
      </template>
      <!-- the heading as Unlock Videos' Masterclass section (Seán, 2026-10-31) -->
      <h2 class="session-title">
        DataVis Masterclass
        <span class="session-count">{{ talkCount }} masterclasses including:</span>
      </h2>
      <ul class="cards">
        <li v-for="speaker in speakers" :key="speaker.name" class="card">
          <!-- opens the speaker dialog (their latest Masterclass year); the link is the
               fallback -->
          <a
            :href="`/${speaker.year}/People#${speaker.name.replace(/ /g, '_')}`"
            class="card-link"
            @click.prevent="openSpeaker(speaker.name, null, speaker.year)">
            <div class="image-wrap">
              <img
                :src="speaker.image"
                :alt="speaker.name"
                class="headshot"
                loading="lazy"
                @error="$event.target.style.display = 'none'" />
            </div>
            <div class="card-info">
              <p class="speaker-line">
                <strong class="speaker-name">{{ speaker.name }}</strong>
                {{ " " }}
                <span class="affiliation">{{ speaker.affiliation }}</span>
              </p>
            </div>
          </a>
          <!-- one line for the speaker's talks (Seán, 2026-10-31): masterclassTalks.json's title;
               a single talk links to its (locked) video, several to the speaker's videos -->
          <p class="talk">
            <router-link :to="talkLink(speaker)" class="talk-link">
              {{ speaker.line.title }}
            </router-link>
          </p>
        </li>
      </ul>
    </div>
  </main>
</template>

<script setup>
import Email from "@/components/Email.vue";
import {computed, nextTick, ref, watch} from "vue";
import {useFitLines} from "@/composables/useFitLines";
import {useFitText} from "@/composables/useFitText";
import {registrationUrl, redirectToRegistration} from "@/composables/useRedirectToRegistration";
import useMasterclassSpeakers from "@/composables/useMasterclassSpeakers";
import useSpeakerDialog from "@/composables/useSpeakerDialog";
import useVideoAccess from "@/composables/useVideoAccess";

const {openSpeaker} = useSpeakerDialog();
const {hasMasterclassAccess} = useVideoAccess();
const {speakers, talkCount, years, stats, isLoading} = useMasterclassSpeakers();
const firstYear = computed(() => Math.min(...years.value));
const lastYear = computed(() => Math.max(...years.value));
// every speaker line (name and affiliation) at one size, the longest fitting on one line, as
// the poster (useFitLines.js)
const rootRef = ref(null);
const lineSize = useFitLines(rootRef, ".speaker-line", speakers);
// the subtitle filling its line
const subtitleRef = ref(null);
const {fit: fitSubtitle} = useFitText(subtitleRef);
watch(isLoading, () => nextTick(fitSubtitle));
// a single talk: its video; several: the speaker's videos
const talkLink = ({line, name}) => {
  return line.hash ? `/Videos/${line.hash}` : `/Videos/${name.replace(/ /g, "_")}`;
};
</script>

<style scoped>
/* the poster block, as Videos/Unlock.vue's */
.unlock-videos {
  position: relative;
  background: black;
  color: white;
  font-family: var(--font-family);
  font-size: clamp(5px, calc((min(100vw, 56rem) - 2rem) / 42.6), 20px);
  box-sizing: border-box;
  padding: 2em 0;
  margin: 0 auto 1.4em auto;
  width: 42.6em;
}

.unlock-content {
  padding: 0 2em;
}

@media (max-width: 600px) {
  .unlock-videos {
    font-size: calc((100vw - 32px) / 38.6);
    width: 38.6em;
  }
  .unlock-content {
    padding-inline: 0;
  }
}

/* as Unlock.vue's */
.poster-title {
  font-size: 2.82em;
  font-weight: 900;
  line-height: 1;
  text-transform: uppercase;
  color: var(--brand-color);
  letter-spacing: 0.04em;
  margin: 0 0 0.18em;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
}

.subtitleFit {
  display: inline-block;
  white-space: nowrap;
}

.vizbi-logo {
  height: 2.7em;
  width: auto;
  transform: translateY(-6%);
}

.poster-subtitle {
  font-size: 1.31em;
  font-weight: 700;
  text-transform: uppercase;
  color: white;
  letter-spacing: 0.06em;
  margin: 0 0 0.76em;
}

.text {
  font-size: 1.32em;
  color: rgba(255, 255, 255, 0.65);
  line-height: 1.5;
  margin: 0 0 1.2em;
  max-width: 60ch;
}

.reg-buttons {
  display: flex;
  flex-direction: column;
  gap: 1.7em;
  margin: 0 2em 2.6em; /* ▶ EDIT HERE: the green button → the rows below it */
}

.reg-btn {
  background: var(--brand-hover-color);
  color: white;
  text-decoration: none;
  text-align: center;
  padding: 0.42em 1.15em;
  font-size: 1.3em;
  font-weight: 400;
  border-radius: 9999px;
}

.reg-btn strong {
  font-weight: 700;
}

/* information, not actions: full-width rows, quieter than the green button, each one link */
.info-rows {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5em;
  margin: 0 0 2.8em;
}

.info-row {
  flex: 1 1 0;
  min-width: max-content;
  white-space: nowrap;
  display: block;
  text-align: center;
  padding: 0.5em 1em;
  font-size: 1em;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.8);
  background: rgba(255, 255, 255, 0.08);
  border-radius: 0.5em;
  text-decoration: none;
}

.info-row:hover {
  background: rgba(255, 255, 255, 0.16);
  color: white;
}

.info-row:focus-visible {
  outline: 2px solid var(--brand-color);
  outline-offset: 2px;
}

.session-title {
  font-size: 1.635em;
  font-weight: 900;
  text-transform: uppercase;
  color: var(--brand-color);
  margin: 0 0 0.5em;
  letter-spacing: 0.02em;
  display: flex;
  align-items: baseline;
  gap: 0.5em;
  flex-wrap: wrap;
}

.session-count {
  font-size: 0.8em;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  color: rgba(255, 255, 255, 0.5);
}

/* every speaker, two to a row as Unlock's featured pairs (Seán, 2026-10-31) */
.cards {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.2em 0.75em;
  margin: 0;
  padding: 0;
  list-style: none;
}

.card {
  min-width: 0;
  list-style: none; /* global.css gives every ul > li a disc */
}

.card-link {
  display: block;
  text-decoration: none;
  color: inherit;
}

.image-wrap {
  width: 100%;
  aspect-ratio: 16 / 9;
  background: black;
  overflow: hidden;
}

.headshot {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.card-info {
  padding-top: 0.4em;
  font-size: 1em;
  line-height: 1.2;
}

.speaker-line {
  margin: 0 0 0.3em; /* ▶ EDIT HERE: the name → the talks below it */
  white-space: nowrap; /* one line, at the size every speaker shares (useFitLines.js) */
  font-size: var(--line-size, 1em);
}

.speaker-name {
  font-weight: 700;
  color: white;
}

.affiliation {
  color: rgba(255, 255, 255, 0.55);
}

.talk {
  margin: 0;
  font-size: 0.95em;
  line-height: 1.3;
}

.talk-link {
  color: var(--brand-color);
  text-decoration: none;
}

.talk-link:hover {
  text-decoration: underline;
  text-underline-offset: 0.15em;
}

.loading {
  color: var(--brand-color);
  padding: 3em;
}
</style>
